import React, { FunctionComponent, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers';
import { ILinkedInUserData } from '../../../interfaces/models/user';
import { AppRoutes } from '../../../interfaces/store/appStore';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { useAppActions } from '../../../stores/application/useAppActions';
import OnboardingStepLayout from '../components/OnboardingStepLayout';
import ConnectLinkedInProfileCard, {
    LINKEDIN_CONNECT_PAGE_SUBTITLE,
} from '../components/ConnectLinkedInProfileCard';
import { isOnboardingSurveyCompleted } from '../onboardingSurveyUtils';
import { useLinkedInProfileAutoCheck } from '../useLinkedInProfileAutoCheck';
import { useOnboardingExtensionRequired } from '../hooks/useOnboardingExtensionRequired';

interface ILinkedInSectionProps {}

const NOT_LOGGED_IN_MESSAGE =
    'Open a new tab, log into your LinkedIn account, then return here and refresh.';

const WRONG_PROFILE_MESSAGE =
    'Open a new tab, log out of your current LinkedIn account, and log in with the correct one. Then return to this page.';

const CONNECT_MIN_FEEDBACK_MS = 600;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const LinkedInSection: FunctionComponent<ILinkedInSectionProps> = () => {
    const { getLoggedInLIUserHandler, saveLinkedInDataToUser } = useAuthHandlers();
    const [userLI, setUserLI] = useState<ILinkedInUserData | undefined>(undefined);
    const [notCorrectProfile, setNotCorrectProfile] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const { store: { user } } = useAuthStore();
    const { setOnboardingStep } = useAppActions();
    const navigate = useNavigate();
    const didNavigateHome = useRef(false);

    useOnboardingExtensionRequired();

    const checkYourLIConnection = useCallback(async () => {
        setLoading(true);
        setNotCorrectProfile(false);
        setStatusMessage('');

        const linkedInUser = await getLoggedInLIUserHandler();

        if (
            !didNavigateHome.current &&
            user?.linkedinData?.publicIdentifier &&
            linkedInUser?.publicIdentifier &&
            linkedInUser.publicIdentifier === user.linkedinData.publicIdentifier
        ) {
            didNavigateHome.current = true;
            if (isOnboardingSurveyCompleted(user)) {
                setOnboardingStep(-2);
                navigate(AppRoutes.HOME);
            } else {
                setOnboardingStep(3);
                navigate(AppRoutes.ONBOARDING_SYNC_NETWORK);
            }
            setLoading(false);
            return;
        }

        if (
            user?.linkedinData?.publicIdentifier &&
            linkedInUser?.publicIdentifier &&
            linkedInUser.publicIdentifier !== user.linkedinData.publicIdentifier
        ) {
            setNotCorrectProfile(true);
            setStatusMessage(WRONG_PROFILE_MESSAGE);
        } else if (!linkedInUser) {
            setStatusMessage(NOT_LOGGED_IN_MESSAGE);
        }

        setUserLI(linkedInUser);
        setLoading(false);
    }, [getLoggedInLIUserHandler, navigate, setOnboardingStep, user?.linkedinData?.publicIdentifier, user?.preferences]);

    const showProfile = Boolean(userLI) && !notCorrectProfile;

    const saveProfileInfo = useCallback(async () => {
        if (!userLI || connecting) return;

        setConnecting(true);
        const startedAt = Date.now();

        try {
            let serverError: string | undefined;
            await saveLinkedInDataToUser(userLI, (message) => { serverError = message; });

            // e.g. this LinkedIn is already connected to too many accounts: say so and stay on this step
            if (serverError) {
                setStatusMessage(serverError);
                return;
            }

            const remaining = CONNECT_MIN_FEEDBACK_MS - (Date.now() - startedAt);
            if (remaining > 0) {
                await wait(remaining);
            }

            if (!isOnboardingSurveyCompleted(user)) {
                setOnboardingStep(3);
                navigate(AppRoutes.ONBOARDING_SYNC_NETWORK);
            }
        } finally {
            setConnecting(false);
        }
    }, [connecting, saveLinkedInDataToUser, userLI, user?.preferences, setOnboardingStep, navigate]);

    useLinkedInProfileAutoCheck({
        enabled: !connecting && !notCorrectProfile,
        profileFound: showProfile,
        onCheck: checkYourLIConnection,
    });

    return (
        <OnboardingStepLayout
            activeStepIndex={1}
            title="Connect your LinkedIn profile"
            subtitle={LINKEDIN_CONNECT_PAGE_SUBTITLE}
        >
            <ConnectLinkedInProfileCard
                userLI={userLI}
                statusMessage={statusMessage}
                loading={loading}
                connecting={connecting}
                showProfile={showProfile}
                onConnect={() => void saveProfileInfo()}
                onRefresh={() => void checkYourLIConnection()}
                onUseDifferentProfile={() => {
                    setNotCorrectProfile(true);
                    setStatusMessage(WRONG_PROFILE_MESSAGE);
                }}
            />
        </OnboardingStepLayout>
    );
};

export default LinkedInSection;
